import { useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";

export default function LaunchSettings({ kind = "coupon" }) {
  const [form, setForm] = useState(null);
  const [fixed, setFixed] = useState([]);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    adminPost("LAUNCH_SETTINGS_GET", { kind }).then((result) => {
      if (!mounted) return;
      if (result.status !== "200" || !result.data?.settings) {
        toast.error(result?.status_message || "Could not load launch settings");
        return;
      }
      const settings = result.data.settings;
      setForm(settings);
      setFixed(settings.fixed || []);
      setOptions(settings.chance_options || []);
    });
    return () => { mounted = false; };
  }, [kind]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const result = await adminPost("LAUNCH_SETTINGS_SAVE", { ...form, kind });
      if (result.status === "200") {
        setForm(result.data.settings);
        toast.success(result.data?.message || "Launch settings saved");
      } else toast.error(result?.data?.error || result?.status_message || "Could not save launch settings");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Box sx={{ p: 3, color: consoleColors.muted }}>Loading launch settings...</Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2, maxWidth: 860 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Launch Parameters</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.4 }}>
          <TextField label="Countdown seconds" type="number" value={form.countdown_seconds} onChange={(event) => setForm({ ...form, countdown_seconds: event.target.value })} helperText="From 3 to 60. The winner is already chosen." />
          <TextField label="Shuffle seconds" type="number" value={form.spin_seconds} onChange={(event) => setForm({ ...form, spin_seconds: event.target.value })} helperText="From 2 to 30. Names shuffle, then the chosen winner stays." />
          <TextField label="Winners each launch" type="number" value={form.winners_per_launch} onChange={(event) => setForm({ ...form, winners_per_launch: event.target.value })} helperText="From 1 to 10. The first name is shown large." />
          <TextField select label="Chance" value={form.chance} onChange={(event) => setForm({ ...form, chance: event.target.value })}>
            {options.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
          {kind === "ticket" && (
            <TextField label="Most coupons in one payment" type="number" value={form.max_buy} onChange={(event) => setForm({ ...form, max_buy: event.target.value })} helperText="From 1 to 50. A new ticket cannot allow more than this." />
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save settings"}</Button>
        </Box>
      </Box>

      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Fixed Launch Rules</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13, mt: 0.4, mb: 1.5 }}>These run automatically. They are not switches.</Typography>
        <Box sx={{ display: "grid", gap: 1.2 }}>
          {fixed.map((rule) => (
            <Box key={rule.title} sx={{ borderTop: `1px solid ${consoleColors.line}`, pt: 1.2 }}>
              <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>{rule.title}</Typography>
              <Typography sx={{ color: consoleColors.muted, fontSize: 13.5, mt: 0.3 }}>{rule.detail}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
